import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Zap, Camera, Tag, ArrowLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { HUB_CONFIG } from '../constants/hubCategories';

interface PostListingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Regex to extract the leading emoji and the clean text title
const parseTitle = (title: string) => {
  const emojiRegex = /^([\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF\u2300-\u23FF\u2B50\u2B06\u2934\u2935\u3297\u3299\u3030\u303D]|\u00ae|\u00a9|[\u2000-\u3300])\s*(.*)$/;
  const match = title.match(emojiRegex);
  if (match) {
    return { emoji: match[1], cleanTitle: match[2] };
  }
  return { emoji: '', cleanTitle: title };
};

const CATEGORIES = Object.entries(HUB_CONFIG).map(([id, config]) => {
  const { emoji, cleanTitle } = parseTitle(config.title);
  return {
    id,
    name: cleanTitle,
    emoji,
    image: config.image,
    description: config.description,
    color: config.color || 'from-orange-500 to-orange-600',
    locationType: config.locationType || 'neighborhood',
  };
});

// Staggered entry animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 260, damping: 25 },
  },
};

export function PostListingModal({ isOpen, onClose }: PostListingModalProps) {
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-6 md:p-8 pt-safe pb-safe">
          {/* High-end Backdrop Blur Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
          />

          {/* Premium Modal Container - Full screen on mobile, styled container on desktop */}
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            variants={{
              desktop: { y: 0, scale: 1 },
              mobile: { y: 0 }
            }}
            transition={{ type: 'spring', damping: 25, stiffness: 220 }}
            className="relative w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-2xl md:max-w-4xl lg:max-w-6xl bg-white rounded-none sm:rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden flex flex-col border-0 sm:border border-slate-100 pb-safe"
          >
            {/* Header Area - Vertical height reduced by 40-50% on mobile */}
            <div className="px-4 py-2.5 pt-[calc(0.625rem+env(safe-area-inset-top))] sm:px-6 sm:py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50/80 via-white to-slate-50/50 shrink-0">
              <div className="flex items-center gap-3 sm:gap-4">
                <button
                  onClick={onClose}
                  className="flex items-center gap-1.5 text-slate-500 hover:text-orange-600 font-extrabold text-xs sm:text-sm transition-colors group bg-transparent border-0 cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                  <span className="hidden sm:inline">Back</span>
                </button>
                <div className="w-px h-5 bg-slate-200 hidden sm:block" />
                <div>
                  <h2 className="text-sm sm:text-xl md:text-2xl font-black text-slate-900 tracking-tight">
                    Post an Ad
                  </h2>
                  <p className="text-slate-500 text-[10px] sm:text-xs font-semibold mt-0.5 block">
                    <span className="sm:hidden">Choose where to post your ads 30 days for free</span>
                    <span className="hidden sm:inline">Choose your hub to start selling on Pambo</span>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl sm:rounded-2xl transition-all bg-transparent border-0 cursor-pointer"
                aria-label="Close"
              >
                <X className="w-5 h-5 sm:w-6 h-6" />
              </button>
            </div>

            {/* Grid Body */}
            <div className="p-3 sm:p-6 lg:p-8 overflow-y-auto hide-scrollbar flex-1 bg-white">
              {/* Unified Responsive Grid View (3 cols on mobile) */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-4"
              >
                {CATEGORIES.map((cat) => (
                  <motion.div key={cat.id} variants={itemVariants} className="h-full">
                    <button
                      onClick={() => {
                        navigate(`/post-listing?hub=${cat.id}`);
                        onClose();
                      }}
                      className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 sm:hover:-translate-y-1.5 transition-all duration-300 group active:scale-[0.98] border border-slate-100 p-0 bg-slate-50 flex flex-col h-full text-left w-full cursor-pointer"
                    >
                      {/* Image Container */}
                      <div className="relative w-full aspect-square overflow-hidden bg-slate-200 shrink-0">
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          referrerPolicy="no-referrer"
                        />
                        {/* Floating Emoji Badge */}
                        <div className="absolute top-1.5 left-1.5 sm:top-2.5 sm:left-2.5 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/95 backdrop-blur shadow flex items-center justify-center text-xs sm:text-base">
                          {cat.emoji}
                        </div>
                        {/* Dark overlay for bottom part of image */}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
                      </div>

                      {/* Text content area */}
                      <div className="p-2 sm:p-3.5 bg-white flex flex-col flex-1 w-full justify-center sm:justify-between">
                        <div>
                          <h4 className="font-extrabold text-slate-800 text-[9px] sm:text-sm leading-tight tracking-tight sm:mb-1 group-hover:text-orange-600 transition-colors line-clamp-2 text-center sm:text-left">
                            {cat.name}
                          </h4>
                          <p className="hidden sm:block text-[10px] text-slate-400 font-semibold line-clamp-2 leading-relaxed">
                            {cat.description}
                          </p>
                        </div>
                        <div className="hidden sm:flex mt-2 pt-2 border-t border-slate-50 items-center justify-end w-full">
                          <span className="text-[10px] font-bold text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                            Start Selling <ChevronRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </button>
                  </motion.div>
                ))}
              </motion.div>

              {/* Bottom Tips Bar */}
              <div className="mt-4 p-3 bg-orange-50/50 rounded-2xl border border-orange-100/70 flex items-center justify-center gap-4 sm:gap-12 flex-wrap">
                <span className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-orange-600">
                  <Camera className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-orange-500" /> Good photos
                </span>
                <span className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-orange-600">
                  <Tag className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-orange-500" /> Fair price
                </span>
                <span className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-orange-600">
                  <Zap className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-orange-500" /> Honest description
                </span>
              </div>
            </div>

            {/* Footer Terms */}
            <div className="px-6 py-3.5 pb-[calc(0.875rem+env(safe-area-inset-bottom))] bg-slate-50 border-t border-slate-100 flex justify-center shrink-0">
              <p className="text-[10px] sm:text-xs text-slate-400 font-semibold">
                By posting, you agree to our{' '}
                <button
                  onClick={() => {
                    navigate('/terms');
                    onClose();
                  }}
                  className="text-orange-600 hover:text-orange-700 hover:underline bg-transparent border-none p-0 cursor-pointer font-bold transition-colors"
                >
                  Pambo Terms of Service
                </button>
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
